/**
 * The insight card library's identity: which cards exist, and the one data
 * shape each of them reads.
 *
 * This lives in shared, not in the app, for one reason: a template can now pin
 * a card on a record's entry list, so `validate-spec.ts` must be able to
 * reject a card id that no client can draw. Two lists would drift, and the
 * drift would only show up as a card silently missing on a device.
 *
 * Everything about how a card LOOKS stays in the app
 * (`apps/mobile/src/lib/insight-cards.ts`): its widths, its name key, whether
 * it plots. Only the id and the shape are a contract.
 *
 * The four registry rules apply here as they do to `components.json`:
 *
 *   1. An id is never renamed and never removed. A stored layout and a stored
 *      view spec both hold these strings.
 *   2. A card is only ever added.
 *   3. A card's shape never changes. The shape is what makes a stored spec
 *      readable, so changing it would silently redraw somebody's card with
 *      the wrong data.
 *   4. A client that meets an id it does not know drops that card and draws
 *      the rest. Nothing ever fails whole.
 *
 * The second half of the file is what a card ASKS FOR: the row source, the
 * condition, and the aggregate tree whose leaves are totals. It lives here
 * rather than in the app for the reason the ids do — the assistant proposes a
 * card, the server has to be able to read the proposal back, and two readers
 * would drift into two products.
 */

import type { CoversAll } from './covers-all';
import { DATE_CONDITION_OPS, dateConditionValueProblem, isDateConditionOp, type DateConditionOp } from './date-conditions';
import {
  isFormulaCalcOp,
  formulaLinesRead,
  readFormulaNode,
  type FormulaCalcOp,
  type FormulaNode,
  type FormulaOnMissing,
} from './formula';

/**
 * The four data shapes an insight can answer, and so the four shapes a card
 * can read.
 *
 *   scalar       one number, optionally the largest group's number.
 *   scalar_pair  the same number twice: this period and the one before it.
 *   series       one number per day.
 *   breakdown    one number per group, ranked.
 */
export const INSIGHT_SHAPES = ['scalar', 'scalar_pair', 'series', 'breakdown'] as const;

export type InsightShape = (typeof INSIGHT_SHAPES)[number];

export const INSIGHT_CARD_IDS = [
  'insight.stat',
  'insight.delta',
  'insight.progress',
  'insight.trend',
  'insight.bars',
  'insight.breakdown',
] as const;

export type InsightCardId = (typeof INSIGHT_CARD_IDS)[number];

/** The one shape each card reads. Never changes for an id that has shipped. */
export const INSIGHT_CARD_SHAPE: Readonly<Record<InsightCardId, InsightShape>> = {
  'insight.stat': 'scalar',
  'insight.delta': 'scalar_pair',
  'insight.progress': 'scalar_pair',
  'insight.trend': 'series',
  'insight.bars': 'series',
  'insight.breakdown': 'breakdown',
};

export const isInsightCardId = (value: unknown): value is InsightCardId =>
  typeof value === 'string' && (INSIGHT_CARD_IDS as readonly string[]).includes(value);

/** Every card that reads one shape. Both halves of the picker read this. */
export const cardsForShape = (shape: InsightShape): readonly InsightCardId[] =>
  INSIGHT_CARD_IDS.filter((id) => INSIGHT_CARD_SHAPE[id] === shape);

/* ------------------------------------------------------------ what a card asks */

/**
 * What one row contributes to a total.
 *
 * Three answers, and the third is the new freedom. `measure` is the record's
 * own measure binding — the amount a record was built around — and it is what
 * every card written before this shape asked for. `field` is one column, which
 * is what the measure sheet has offered since insights shipped. `expr` is a
 * row formula: the same closed tree a formula field holds, worked out per row
 * and then added up, so "total of litres times rate" needs no stored column
 * and no second grammar.
 *
 * `onMissing` is the formula's own forgiveness rule, carried here because an
 * expr measure has no field spec to keep it in.
 */
export type InsightRowSource =
  | { kind: 'measure' }
  | { kind: 'field'; fieldId: string }
  | { kind: 'expr'; expr: FormulaNode; onMissing?: FormulaOnMissing };

/**
 * What a card's condition may ask. Closed, the way a link condition's op set is.
 *
 * `in` and `not_in` are "is any of" and "is none of": the same question as
 * `is` and `is_not`, asked of several values at once. They carry a list, and
 * only they do, so a card written before they existed reads exactly as it did.
 */
export type InsightFilterOp =
  | 'is'
  | 'is_not'
  | 'in'
  | 'not_in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'set'
  | 'unset'
  // The date conditions of F21 (docs/plans/field-relations.md), on any stored
  // date, worked out with the device's day: `date-conditions.ts`.
  | DateConditionOp;

/** Every where op, in the order of the type. */
export const INSIGHT_WHERE_OPS = [
  'is',
  'is_not',
  'in',
  'not_in',
  'gt',
  'gte',
  'lt',
  'lte',
  'set',
  'unset',
  ...DATE_CONDITION_OPS,
] as const satisfies readonly InsightFilterOp[];
true satisfies CoversAll<InsightFilterOp, typeof INSIGHT_WHERE_OPS>;

const INSIGHT_FILTER_OPS: ReadonlySet<string> = new Set(INSIGHT_WHERE_OPS);

/** The two ops that ask about presence, and so carry no value. */
const PRESENCE_OPS: ReadonlySet<string> = new Set(['set', 'unset']);

/** The two ops that ask about several values, and so carry a list. */
const LIST_OPS: ReadonlySet<string> = new Set(['in', 'not_in']);

/** True for "is any of" and "is none of", the ops whose value is a list. */
export const isListOp = (op: InsightFilterOp): op is 'in' | 'not_in' => LIST_OPS.has(op);

/** One value a condition compares against. */
export type InsightWhereValue = string | number;

/**
 * How many values one "is any of" may hold. Each is one bound parameter, and a
 * list longer than this is a question better asked the other way round.
 */
export const INSIGHT_WHERE_MAX_VALUES = 50;

/**
 * A condition's values, as a list whatever it holds: none for `set`/`unset`,
 * one for a single-value op, and the list itself for `in`/`not_in`.
 */
export const whereValues = (where: InsightWhere): readonly InsightWhereValue[] => {
  if (where.value === undefined) return [];
  return Array.isArray(where.value) ? (where.value as readonly InsightWhereValue[]) : [where.value as InsightWhereValue];
};

/**
 * One condition on the rows a card counts.
 *
 * `via` is a reference field on the record, and then `fieldId` names a field on
 * the row it points at: "only the customers on credit terms". Depth stays one
 * hop, which the compiler draws as one scalar subquery.
 */
export type InsightWhere = {
  fieldId: string;
  op: InsightFilterOp;
  /**
   * Absent on `set` and `unset`, which ask about one side only. A list on `in`
   * and `not_in`, and only there; one value on every other op.
   */
  value?: InsightWhereValue | readonly InsightWhereValue[];
  via?: string;
};

/** How the rows are added up. `rows` counts rows; `count` counts filled values. */
export type InsightAgg = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'rows';

const AGGS: ReadonlySet<string> = new Set(['sum', 'avg', 'min', 'max', 'count', 'rows']);

/**
 * One total: what to add up, over which record's rows, under which conditions.
 *
 * `recordId` absent means this record, which is every card on a record's own
 * summary. Naming another record is what puts a card on the Dashboard, because
 * only the Dashboard hosts a card whose leaves reach across two records.
 *
 * `groupFieldId` is what a breakdown's keys line up on. A combined breakdown is
 * only allowed when every leaf groups by a reference into the SAME directory,
 * which is the caller's check and not this reader's: the reader knows shapes,
 * not schemas.
 */
export type InsightAggLeaf = {
  kind: 'agg';
  agg: InsightAgg;
  /** Every agg but `rows` needs one: a row count reads no value. */
  of?: InsightRowSource;
  recordId?: string;
  filters?: readonly InsightWhere[];
  groupFieldId?: string;
};

/**
 * The aggregate tree: totals at the leaves, arithmetic above them.
 *
 * "What each customer owes" is one leaf minus another. "The average rate" is
 * total amount over total litres. Neither is a row formula, because neither can
 * be worked out on one row — which is exactly why this is a second tree and not
 * a fourth home for the first one.
 */
export type InsightAggNode =
  | InsightAggLeaf
  | { kind: 'const'; value: number }
  | { kind: 'calc'; op: FormulaCalcOp; left: InsightAggNode; right: InsightAggNode }
  | { kind: 'round'; of: InsightAggNode; decimals: number };

/**
 * How many totals one card may run, and how deep the arithmetic over them may
 * nest.
 *
 * Each leaf is one subquery the phone runs against its mirror while a card is
 * on screen, so the cap is a frame budget and not a matter of taste. Eight is
 * more leaves than any card anybody has drawn; six is deeper than any sum a
 * person states in one sentence.
 */
export const INSIGHT_AGG_MAX_LEAVES = 8;
export const INSIGHT_AGG_MAX_DEPTH = 6;

/* --------------------------------------------------------- reading them back */

/**
 * A stored row source, read back without trusting it.
 *
 * Null means this version cannot read the shape, and the rule of the four
 * registry rules above applies: the card is dropped and the rest are drawn.
 * Never guessed at — a card drawn from a shape half-understood shows a number,
 * and a wrong number is worse than a missing card.
 */
export const readRowSource = (raw: unknown): InsightRowSource | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { kind?: unknown; fieldId?: unknown; expr?: unknown; onMissing?: unknown };

  if (held.kind === 'measure') return { kind: 'measure' };

  if (held.kind === 'field') {
    return typeof held.fieldId === 'string' && held.fieldId !== ''
      ? { kind: 'field', fieldId: held.fieldId }
      : null;
  }

  if (held.kind === 'expr') {
    const expr = readFormulaNode(held.expr);
    if (expr === null) return null;
    // A total over chosen rows is a total inside a row, and a measure is
    // itself totalled over rows: an insight measure may not hold one.
    if (formulaLinesRead({ result: 'number', expr, onMissing: 'blank' }).length > 0) return null;
    // Anything but the two words reads as absent, exactly as `readFormulaSpec`
    // treats it: the default is the safe one, and it is `blank`.
    if (held.onMissing === 'zero') return { kind: 'expr', expr, onMissing: 'zero' };
    if (held.onMissing === 'blank') return { kind: 'expr', expr, onMissing: 'blank' };
    return { kind: 'expr', expr };
  }

  return null;
};

/** One stored condition, read back the same way. */
export const readInsightWhere = (raw: unknown): InsightWhere | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { fieldId?: unknown; op?: unknown; value?: unknown; via?: unknown };

  if (typeof held.fieldId !== 'string' || held.fieldId === '') return null;
  if (typeof held.op !== 'string' || !INSIGHT_FILTER_OPS.has(held.op)) return null;
  const op = held.op as InsightFilterOp;
  if (held.via !== undefined && (typeof held.via !== 'string' || held.via === '')) return null;
  const hop = held.via === undefined ? {} : { via: held.via as string };

  if (PRESENCE_OPS.has(op)) return { fieldId: held.fieldId, op, ...hop };
  if (isDateConditionOp(op)) {
    // A date condition carries a count of days, a date, or nothing, read as it is judged.
    if (dateConditionValueProblem(op, held.value) !== null) return null;
    if (held.value === undefined || held.value === null) return { fieldId: held.fieldId, op, ...hop };
    if (!isWhereValue(held.value)) return null;
    return { fieldId: held.fieldId, op, value: held.value, ...hop };
  }
  if (LIST_OPS.has(op)) {
    // "Is any of" nothing is a card that can never count a row, so it is not
    // read as one. Repeats are dropped, keeping the first order given.
    if (!Array.isArray(held.value) || held.value.length === 0) return null;
    if (held.value.length > INSIGHT_WHERE_MAX_VALUES) return null;
    const values: InsightWhereValue[] = [];
    const seen = new Set<string>();
    for (const one of held.value as unknown[]) {
      if (!isWhereValue(one)) return null;
      const key = `${typeof one}:${String(one)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      values.push(one);
    }
    return { fieldId: held.fieldId, op, value: values, ...hop };
  }
  // "More than" with nothing to be more than is a question nobody can answer.
  if (!isWhereValue(held.value)) return null;
  return { fieldId: held.fieldId, op, value: held.value, ...hop };
};

const isWhereValue = (value: unknown): value is InsightWhereValue =>
  typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));

/**
 * A stored aggregate tree, read back with its two caps proved.
 *
 * The leaf count is counted across the whole tree rather than per branch,
 * because it is the number of subqueries that costs the frame.
 */
export const readInsightAgg = (raw: unknown): InsightAggNode | null => {
  const tally = { leaves: 0 };
  const read = readAggNode(raw, 1, tally);
  return read === null || tally.leaves > INSIGHT_AGG_MAX_LEAVES ? null : read;
};

const readAggNode = (raw: unknown, depth: number, tally: { leaves: number }): InsightAggNode | null => {
  if (depth > INSIGHT_AGG_MAX_DEPTH) return null;
  if (tally.leaves > INSIGHT_AGG_MAX_LEAVES) return null;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as Record<string, unknown>;

  if (held.kind === 'agg') return readAggLeaf(held, tally);

  if (held.kind === 'const') {
    return typeof held.value === 'number' && Number.isFinite(held.value)
      ? { kind: 'const', value: held.value }
      : null;
  }

  if (held.kind === 'calc') {
    if (!isFormulaCalcOp(held.op)) return null;
    const left = readAggNode(held.left, depth + 1, tally);
    const right = readAggNode(held.right, depth + 1, tally);
    if (left === null || right === null) return null;
    return { kind: 'calc', op: held.op, left, right };
  }

  if (held.kind === 'round') {
    const of = readAggNode(held.of, depth + 1, tally);
    if (of === null) return null;
    if (typeof held.decimals !== 'number' || !Number.isFinite(held.decimals)) return null;
    return { kind: 'round', of, decimals: Math.min(8, Math.max(0, Math.trunc(held.decimals))) };
  }

  return null;
};

const readAggLeaf = (held: Record<string, unknown>, tally: { leaves: number }): InsightAggLeaf | null => {
  tally.leaves += 1;
  if (tally.leaves > INSIGHT_AGG_MAX_LEAVES) return null;

  if (typeof held.agg !== 'string' || !AGGS.has(held.agg)) return null;
  const agg = held.agg as InsightAgg;

  if (held.recordId !== undefined && (typeof held.recordId !== 'string' || held.recordId === '')) return null;
  const record = held.recordId === undefined ? {} : { recordId: held.recordId as string };

  if (held.groupFieldId !== undefined && (typeof held.groupFieldId !== 'string' || held.groupFieldId === '')) {
    return null;
  }
  const group = held.groupFieldId === undefined ? {} : { groupFieldId: held.groupFieldId as string };

  let filters: InsightWhere[] | undefined;
  if (held.filters !== undefined) {
    if (!Array.isArray(held.filters)) return null;
    filters = [];
    for (const one of held.filters) {
      const read = readInsightWhere(one);
      if (read === null) return null;
      filters.push(read);
    }
  }
  const where = filters === undefined ? {} : { filters };

  // A row count reads no value, so it names none. Every other total does: a
  // sum of nothing is not a zero, it is a card nobody can draw.
  if (agg === 'rows') return { kind: 'agg', agg, ...record, ...where, ...group };
  const of = readRowSource(held.of);
  if (of === null) return null;
  return { kind: 'agg', agg, of, ...record, ...where, ...group };
};
